import React, { useState, useRef, useEffect } from 'react';
import { X, GripHorizontal } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Draggable Dialog Component
 * A modal dialog that can be dragged around the screen
 * Positioned at top-left of content area (below tabs)
 */
export default function DraggableDialog({
  open,
  onClose,
  title,
  children,
  maxWidth = 'max-w-lg',
}) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dialogRef = useRef(null);

  // Reset position when dialog opens
  useEffect(() => {
    if (open) {
      setPosition({ x: 0, y: 0 });
    }
  }, [open]);

  const handleMouseDown = (e) => {
    if (dialogRef.current) {
      setIsDragging(true);
      const rect = dialogRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && dialogRef.current) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      setPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  if (!open) return null;

  // Calculate initial position - below tabs area 
  // 700px from left (after sidebar + left content area), 180px from top (below header + tabs)
  const initialLeft = 700;
  const initialTop = 180;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30"
        onClick={onClose}
      />
      
      {/* Dialog - Positioned at top-left of content area */}
      <div
        ref={dialogRef}
        className={cn(
          'fixed bg-white rounded-xl shadow-2xl border border-slate-200',
          'w-full',
          maxWidth,
          isDragging ? 'cursor-grabbing' : ''
        )}
        style={{
          left: position.x || initialLeft,
          top: position.y || initialTop,
          maxHeight: 'calc(100vh - 220px)',
        }}
      >
        {/* Header - Draggable area */}
        <div
          className={cn(
            'flex items-center justify-between px-6 py-4 border-b border-slate-200',
            'cursor-grab select-none',
            isDragging && 'cursor-grabbing'
          )}
          onMouseDown={handleMouseDown}
        >
          <div className="flex items-center gap-2">
            <GripHorizontal className="w-4 h-4 text-slate-400" />
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
              {title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="dialog-close-btn"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
